/**
 * Shared date utility functions for calendar components.
 *
 * Every function here takes the profile `timezone` and interprets its `Date`
 * arguments as calendar days in that zone — see `calendarDays.ts` for the
 * representation these are built on. Nothing in this file reads the browser's
 * calendar.
 */

import {
  DEFAULT_TIME_FORMAT,
  formatTime,
  formatTimeCompact,
  type TimeFormat,
} from '@/lib/time';

import { getWeekStartDay } from '../hooks/useCalendarData';
import {
  addCalendarDays,
  addCalendarMonths,
  calendarDayParts,
  lastDayOfCalendarMonth,
  startOfCalendarMonth,
  startOfCalendarWeek,
} from './calendarDays';

/** Milliseconds in a civil (UTC-arithmetic) day. */
const CIVIL_DAY_MS = 86_400_000;

/** Day names starting with Sunday */
export const DAY_NAMES_SUNDAY_START = [
  'Sun',
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
];

/** Day names starting with Monday */
export const DAY_NAMES_MONDAY_START = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun',
];

/**
 * Format event time for display (e.g., "9:00 AM" / "14:30"). Thin delegate of
 * the canonical `@/lib/time` formatter — don't add formatting branches here.
 *
 * `timezone` is the profile timezone the whole grid is drawn in: an event
 * positioned at the 10:30 row must also be LABELLED 10:30, and the label is
 * what the browser-local reading got wrong.
 */
export function formatEventTime(
  dateString: string,
  format: TimeFormat = DEFAULT_TIME_FORMAT,
  timezone?: string
): string {
  return formatTime(dateString, format, {
    locale: 'en-US',
    timeZone: timezone,
  });
}

/**
 * Compact time label for the MonthView's inline event rows where the standard
 * "8:00 AM" format is too verbose. Thin delegate of the canonical
 * `@/lib/time` compact formatter ("8am"/"4:30pm" ↔ "8:00"/"14:30").
 * Same `timezone` contract as `formatEventTime`.
 */
export function formatEventTimeShort(
  dateString: string,
  format: TimeFormat = DEFAULT_TIME_FORMAT,
  timezone?: string
): string {
  return formatTimeCompact(dateString, format, timezone);
}

/**
 * Hour labels for the calendar time-grid gutters (Week/Day views), honoring
 * the org's time-format setting.
 *
 * - 12-hour orgs: "12 AM", "1 AM" … "12 PM" … "11 PM"
 * - 24-hour orgs: "00:00", "01:00" … "23:00" — 2-digit hour to match how
 *   times render across the rest of the app in 24-hour mode (timeParts uses
 *   a '2-digit' hour for 24h)
 *
 * Returns one label per hour, index 0–23.
 */
export function hourLabelsFor(
  format: TimeFormat = DEFAULT_TIME_FORMAT
): string[] {
  return Array.from({ length: 24 }, (_, i) => {
    if (format === '24h') {
      return `${i.toString().padStart(2, '0')}:00`;
    }
    if (i === 0) {
      return '12 AM';
    }
    if (i === 12) {
      return '12 PM';
    }
    if (i < 12) {
      return `${i} AM`;
    }
    return `${i - 12} PM`;
  });
}

/**
 * Calendar view type definition.
 */
export type CalendarView = 'month' | 'week' | 'day';

/**
 * Get the week number for a date, consistent with locale-based week start.
 * Uses the same week start day as getWeekStartDay() for consistency.
 */
export function getWeekNumber(date: Date, timezone: string): number {
  const weekStartDay = getWeekStartDay();
  const { year, month, day } = calendarDayParts(date, timezone);

  // Week numbering is pure civil arithmetic once the calendar day is known —
  // done in UTC so a DST boundary inside the year cannot shift a week.
  const dayUtc = Date.UTC(year, month - 1, day);
  const weekStartUtc =
    dayUtc -
    ((new Date(dayUtc).getUTCDay() - weekStartDay + 7) % 7) * CIVIL_DAY_MS;

  const januaryFirstUtc = Date.UTC(year, 0, 1);
  const firstWeekStartUtc =
    januaryFirstUtc +
    ((weekStartDay - new Date(januaryFirstUtc).getUTCDay() + 7) % 7) *
      CIVIL_DAY_MS;

  // A partial week at the start of the year counts as week 1.
  if (weekStartUtc < firstWeekStartUtc) {
    return 1;
  }
  return (
    Math.floor((weekStartUtc - firstWeekStartUtc) / (7 * CIVIL_DAY_MS)) + 1
  );
}

/**
 * Get a formatted week number string for display.
 * For month view, returns "W{start}-{end}" spanning all weeks in the month.
 * For week/day view, returns "W{n}" for the single week.
 */
export function formatWeekLabel(
  date: Date,
  view: CalendarView,
  timezone: string
): string {
  if (view === 'month') {
    const startWeek = getWeekNumber(
      startOfCalendarMonth(date, timezone),
      timezone
    );
    const endWeek = getWeekNumber(
      lastDayOfCalendarMonth(date, timezone),
      timezone
    );
    return `W${startWeek}–${endWeek}`;
  }
  return `W${getWeekNumber(date, timezone)}`;
}

/**
 * "September 2026" for a calendar day, in `timezone`.
 *
 * The options object is inlined at every call below rather than hoisted: the
 * `no-restricted-syntax` timezone ratchet reads the literal `timeZone:` key at
 * the call site, and a hoisted variable reads to it as a missing timezone.
 */
function formatMonthYear(date: Date, timezone: string): string {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: timezone,
  });
}

/** "Sep 1" for a calendar day, in `timezone`. */
function formatMonthDay(date: Date, timezone: string): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
  });
}

/**
 * Format the title based on view type.
 */
export function formatTitle(
  date: Date,
  view: CalendarView,
  timezone: string
): string {
  switch (view) {
    case 'month':
      return formatMonthYear(date, timezone);
    case 'week':
      return formatMonthYear(
        startOfCalendarWeek(date, getWeekStartDay(), timezone),
        timezone
      );
    case 'day':
      return formatMonthYear(date, timezone);
    default:
      return '';
  }
}

/**
 * Format the date range subtitle based on view type.
 */
export function formatDateRange(
  date: Date,
  view: CalendarView,
  timezone: string
): string {
  switch (view) {
    case 'month': {
      const firstDay = startOfCalendarMonth(date, timezone);
      const lastDay = lastDayOfCalendarMonth(date, timezone);
      return `${formatMonthDay(firstDay, timezone)} - ${formatMonthDay(lastDay, timezone)}`;
    }
    case 'week': {
      const weekStart = startOfCalendarWeek(date, getWeekStartDay(), timezone);
      const weekEnd = addCalendarDays(weekStart, 6, timezone);
      return `${formatMonthDay(weekStart, timezone)} - ${formatMonthDay(weekEnd, timezone)}`;
    }
    case 'day':
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        timeZone: timezone,
      });
    default:
      return '';
  }
}

/**
 * Navigate to previous period based on view type.
 */
export function navigatePrev(
  date: Date,
  view: CalendarView,
  timezone: string
): Date {
  switch (view) {
    case 'month':
      return addCalendarMonths(date, -1, timezone);
    case 'week':
      return addCalendarDays(date, -7, timezone);
    case 'day':
      return addCalendarDays(date, -1, timezone);
    default:
      return date;
  }
}

/**
 * Navigate to next period based on view type.
 */
export function navigateNext(
  date: Date,
  view: CalendarView,
  timezone: string
): Date {
  switch (view) {
    case 'month':
      return addCalendarMonths(date, 1, timezone);
    case 'week':
      return addCalendarDays(date, 7, timezone);
    case 'day':
      return addCalendarDays(date, 1, timezone);
    default:
      return date;
  }
}
